/**
 * ConnectedAccountRow — one connected account on the per-capability detail page.
 *
 * Renders a single glassy row for one ``ConnectionSummary`` (Gmail account,
 * Slack workspace, Salesforce org, …). Stacked under the per-capability
 * heading on ``/workspace/integrations/$capability``; the consumer wraps
 * a list of these in a ``<ul role="list">``.
 *
 * Pure presentation — owns no mutation state. Every action (manage,
 * reauth, sync, disconnect) is a callback prop; the caller decides whether
 * to invalidate queries, open a confirm dialog, redirect through OAuth,
 * etc. The component only exposes:
 *
 *   1. The visual contract (status dot, vendor mark, italian-relative
 *      ``last_used_at`` line).
 *   2. The kebab menu chrome (Radix Popover, ``role="menu"``).
 *   3. The ``needs_reauth`` warning variant (amber border + token-scaduto
 *      pill + primary-tinted "Riconnetti" CTA).
 *
 * Brand colours flow through the shared ``vendorIcon`` lookup; no inline
 * hex. The only ``Plug`` fallback is the lucide stand-in for unknown
 * vendors (catalog drift safety net).
 */
import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { MoreVertical, Plug, RefreshCcw, Settings, Unplug } from "lucide-react";
import * as React from "react";

import { type ConnectionSummary, type DriveProvider, asDriveProvider } from "@ando/core";
import {
  Button,
  DelegationStatusChip,
  Popover,
  PopoverContent,
  PopoverTrigger,
  asDelegationStatus,
  cn,
} from "@ando/ui";

import { type SyncSource, SyncStatusPill } from "@/features/sync";
import { useDriveSyncStatus } from "@/features/sync/hooks/useDriveSync";
import { formatRelativeTime } from "../../workspace/hooks/formatRelativeTime";
import { getConnectorManifest } from "../manifest";
import { AutoSyncBadge } from "./AutoSyncBadge";
import { CancelSyncButton } from "./CancelSyncButton";
import { ConnectorStatus } from "./ConnectorStatus";
import { vendorIcon, vendorLabel } from "./vendorIcons";

// ── DriveAutoSyncSubLine ───────────────────────────────────────────────
// Mounted only for ``drive`` rows with a recognised provider. Reads the
// per-account auto-sync state from ``/drive-management/sync-status`` via
// the existing ``useDriveSyncStatus`` query and renders the V1-parity
// 3-state badge (V1 audit G1). Hidden when the row is in needs_reauth
// (the reconnect CTA takes precedence over scheduling chrome).
//
// Source of truth for the recognised drive provider IDs lives in
// ``@ando/core/api/endpoints/driveManagement`` — ``asDriveProvider`` is
// the narrowing helper (returns ``DriveProvider | null``), shared with
// the picker + sync filters + every other drive consumer. Adding a 5th
// vendor there lights up the badge here for free.

const DriveAutoSyncSubLine: React.FC<{
  readonly provider: DriveProvider;
  readonly connectionKey: string | null;
}> = ({ provider, connectionKey }) => {
  // ``useDriveSyncStatus`` is the canonical TanStack wrapper; it dedupes
  // requests per (provider, connectionKey) so two rows for the same
  // account never double-fetch. The hook returns the unwrapped data (or
  // ``null`` until the query resolves), not the TanStack ``{ data }``
  // envelope — DRY across every drive consumer.
  const status = useDriveSyncStatus(provider, connectionKey ?? undefined);
  return (
    <AutoSyncBadge
      autoSyncEnabled={status?.auto_sync_enabled}
      autoSyncEligible={status?.auto_sync_eligible}
      autoSyncIntervalMinutes={status?.auto_sync_interval_minutes}
      nextAutoSyncAt={status?.next_auto_sync_at}
      filesIndexed={status?.files_indexed}
    />
  );
};

export interface ConnectedAccountRowProps {
  readonly connection: ConnectionSummary;
  /**
   * Fires when the user clicks the primary ``Gestisci →`` CTA. This is
   * the single entry point to the filter sheet — the kebab no longer
   * exposes a "Sincronizza ora" shortcut. Single, clear path:
   * Gestisci → filtri → Sincronizza N elementi.
   */
  readonly onManage?: () => void;
  /** Fires when the user clicks "Riconnetti" (token expired or kebab). */
  readonly onReauth?: () => void;
  /** Fires when the user clicks "Disconnetti" — caller shows the confirm dialog. */
  readonly onDisconnect?: () => void;
  /** Mark the row as busy (mutation in flight). Disables the kebab + dims the chip. */
  readonly busy?: boolean;
  /**
   * Extra menu items rendered above the default "Riconnetti" entry —
   * lets per-capability hosts (DB/ERP) inject management actions
   * (Esplora schema, Modifica connessione, …) without forking the
   * shared row.
   *
   * Render-prop form: the caller receives a ``close`` callback to
   * dismiss the popover before their handler fires. A separator is
   * rendered automatically below the injected block.
   */
  readonly extraMenuItems?: (close: () => void) => React.ReactNode;
}

// Vendor label resolved via the single source of truth in ``vendorIcons.tsx``
// (``VENDOR_LABEL`` map + Title-Case fallback). Kept as a one-liner so the
// call sites below read naturally.

// ── Filesystem per-folder sync outcome ──────────────────────────────────
// The generic meta line ("vendor · sync Nfa · Attivo") describes the TOKEN,
// not whether THIS folder actually indexed. For the desktop filesystem
// connector we surface the real outcome from the connection metadata so the
// user can see at a glance: synced OK (N files), nothing found, failed, or
// never run. DRY: one derivation, five visual states, design-token tones.
interface FsSyncView {
  readonly basename: string;
  readonly parentPath: string | null;
  readonly fullPath: string | null;
  readonly dotClass: string;
  readonly textClass: string;
  readonly copy: string;
  readonly isFailure: boolean;
}
function deriveFsSyncView(
  connection: ConnectionSummary,
  relative: string | null,
  i18n: I18n,
): FsSyncView {
  const m = (connection.metadata ?? {}) as Record<string, unknown>;
  const files = typeof m.total_files_indexed === "number" ? m.total_files_indexed : 0;
  const status = typeof m.last_sync_status === "string" ? m.last_sync_status : null;
  const basePaths = Array.isArray(m.base_paths) ? (m.base_paths as string[]) : [];
  const fullPath = basePaths[0] ?? null;
  const basename =
    fullPath?.split("/").filter(Boolean).pop() ??
    connection.connection_key ??
    i18n._(msg`Cartella`);
  const cut = fullPath ? fullPath.lastIndexOf("/") : -1;
  const parentPath = fullPath && cut > 0 ? fullPath.slice(0, cut) : null;
  const when = relative ? i18n._(msg`${relative} fa`) : "";
  const base = { basename, parentPath, fullPath };
  if (!connection.last_used_at)
    return {
      ...base,
      dotClass: "bg-ink-3",
      textClass: "text-ink-3",
      copy: i18n._(msg`In attesa di sincronizzazione`),
      isFailure: false,
    };
  if (status === "failed")
    return {
      ...base,
      dotClass: "bg-error",
      textClass: "text-error",
      copy: i18n._(msg`Sincronizzazione fallita · riprova`),
      isFailure: true,
    };
  if (files === 0)
    return {
      ...base,
      dotClass: "bg-warning",
      textClass: "text-warning",
      copy: i18n._(msg`Nessun file trovato · ${when}`),
      isFailure: false,
    };
  return {
    ...base,
    dotClass: "bg-success",
    textClass: "text-success",
    copy: i18n._(msg`${files.toLocaleString("it-IT")} file indicizzati · ${when}`),
    isFailure: false,
  };
}

export const ConnectedAccountRow: React.FC<ConnectedAccountRowProps> = ({
  connection,
  onManage,
  onReauth,
  onDisconnect,
  busy = false,
  extraMenuItems,
}) => {
  const { i18n } = useLingui();
  const [menuOpen, setMenuOpen] = React.useState(false);

  // Manifest-driven actions (Jul 2026): which surface "Gestisci" opens (or
  // ``null`` → no primary button), whether the connector can reauth, and how
  // to label the destructive action. Replaces the ad-hoc ``isFilesystem``
  // action branches + the dead-end "coming soon" toast.
  const manifest = getConnectorManifest(connection.capability);

  const { vendor, provider, connection_key, needs_reauth, last_used_at, metadata } = connection;
  // June 13 2026 — surface the BE lifecycle hint (``metadata.status``) so a
  // spreadsheet connector still ingesting in the background renders as
  // "Sincronizzazione in corso…" instead of the default "Attivo" label.
  // Drives the dot tone + copy + (downstream) ``ConnectorStatus``. Single source
  // of truth: ``_row_to_connector_response`` on the BE → ``mapEnterpriseToSummary``
  // on the FE puts the value at ``metadata.status``.
  const lifecycleStatus =
    metadata &&
    typeof metadata === "object" &&
    typeof (metadata as Record<string, unknown>).status === "string"
      ? ((metadata as Record<string, unknown>).status as string)
      : null;
  // June 4 2026 — V1 audit (G2). The BE's ``_summarise_row`` omits
  // ``vendor`` for public multi-provider capabilities (drive, email,
  // messaging…) and surfaces ``provider`` instead (the user-facing SaaS:
  // ``google_drive`` / ``onedrive`` / ``sharepoint`` / ``notion`` /
  // ``gmail`` / ``outlook`` / ``slack`` / ``teams``). Reading just
  // ``connection.vendor`` produced 5 generic plug icons in the user's
  // "Documenti cloud" screenshot — none of the providers matched the
  // ``VENDORS`` map because ``vendor`` was undefined. Same pattern
  // already in ``SpreadsheetSourceStep.tsx:159`` (``c.provider ?? c.vendor``).
  const vendorKey = provider ?? vendor;
  const label = vendorLabel(vendorKey);
  const sendOnly = connection.access_mode === "send_only" || connection.connection_type === "email_send";
  const accountLabel = `${connection.email_address ?? connection_key ?? connection.conn_id}${sendOnly ? ` · ${i18n._(msg`Solo invio`)}` : ""}`;
  const relative = formatRelativeTime(last_used_at);
  const isFilesystem = connection.capability === "filesystem";
  const fsView = isFilesystem ? deriveFsSyncView(connection, relative, i18n) : null;
  // Generic per-vendor delegation badge — surfaces ``metadata.delegation_status``
  // when the BE flags one (Cassetto AdE, PEC opt-in, …). The chip is
  // capability-agnostic so any future delegated-auth flow drops in for free.
  const delegationStatus = asDelegationStatus(
    metadata && typeof metadata === "object" && "delegation_status" in metadata
      ? (metadata as { delegation_status?: unknown }).delegation_status
      : null,
  );

  // Stable predicate for the sync pill + cancel button — both subscribe
  // via ``useLatestJobFor(source, match)`` and we don't want every parent
  // re-render to thrash the selector's referential equality check.
  //
  // Narrow the deps to the ONLY fields ``matchesConnection`` reads
  // (``connection_key`` / ``provider`` / ``vendor``) + ``capability``
  // (drives ``syncSourceFor``). Heartbeat-style updates on
  // ``last_used_at`` / ``metadata.status`` / ``needs_reauth`` no longer
  // invalidate the predicate identity — reviewer #6 (June 4 2026).
  const syncSource = React.useMemo(() => syncSourceFor(connection), [connection.capability]);
  const connectionKeyForMatch = connection.connection_key;
  const providerForMatch = connection.provider;
  const vendorForMatch = connection.vendor;
  const matchJob = React.useCallback(
    (id: { provider?: string; email_address?: string; connection_key?: string }) =>
      matchesConnection(id, {
        connection_key: connectionKeyForMatch,
        provider: providerForMatch,
        vendor: vendorForMatch,
      }),
    [connectionKeyForMatch, providerForMatch, vendorForMatch],
  );

  // Brand mark — prefer the curated SVG; fall back to the generic ``Plug``
  // chip when the vendor isn't in the lookup (catalog drift safety net).
  const brand = vendorIcon(vendorKey, "h-6 w-6");
  const badge =
    brand !== null ? (
      <span
        aria-hidden
        className={cn(
          "flex h-11 w-11 shrink-0 items-center justify-center rounded-md",
          needs_reauth ? "opacity-70" : undefined,
        )}
      >
        {brand}
      </span>
    ) : (
      <span
        aria-hidden
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-ink-3/10 text-ink-3"
      >
        <Plug className="h-6 w-6" />
      </span>
    );

  // ── Kebab menu handlers ──────────────────────────────────────────────
  // Each item closes the popover before delegating to the caller so the
  // open state never lingers behind a modal/confirm dialog the caller
  // may want to mount in response. Kebab now exposes ONLY reauth +
  // disconnect; sync runs exclusively through the ``Gestisci →`` filter
  // sheet (single, clear path, no shortcut ambiguity).
  const handleReauthFromMenu = () => {
    setMenuOpen(false);
    onReauth?.();
  };
  const handleDisconnect = () => {
    setMenuOpen(false);
    onDisconnect?.();
  };

  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors",
        needs_reauth
          ? "border-warning/40 bg-warning/[0.04] hover:bg-warning/[0.06]"
          : "border-hairline bg-card/60 hover:bg-card/85",
        busy && "opacity-60",
      )}
    >
      {badge}

      <div className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-center gap-1.5">
          <span className="truncate font-medium text-[13px] text-ink-1">
            {fsView ? fsView.basename : accountLabel}
          </span>
          {/* Live sync status — lights up while a job for THIS connection
              is pending / running / failed. Subscribes to the unified
              ``SyncStore`` via ``useLatestJobFor``. Hidden when idle. */}
          {!sendOnly ? <SyncStatusPill source={syncSource} match={matchJob} /> : null}
          {/* V1 audit G8 — inline Stop Sync trigger while a job is
              running. The button is gated by ``useLatestJobFor`` (same
              source/match predicate as the pill) so it appears next to
              the pill only when there's something to stop. */}
          {!sendOnly ? <CancelSyncButton source={syncSource} match={matchJob} /> : null}
          {delegationStatus !== null ? <DelegationStatusChip status={delegationStatus} /> : null}
        </span>

        {/* Filesystem: dimmed FULL path under the folder basename (Aug 26,
            2026 — was the parent dir only, so two sibling folders showed the
            IDENTICAL path and were indistinguishable at a glance). */}
        {fsView?.fullPath ? (
          <span className="truncate text-[10px] text-ink-3" title={fsView.fullPath}>
            {fsView.fullPath}
          </span>
        ) : null}

        {fsView ? (
          // Filesystem: real per-folder sync outcome (5 states, token tones).
          <span
            className={cn("flex items-center gap-1.5 truncate text-[11px]", fsView.textClass)}
            {...(fsView.isFailure ? { role: "status" as const } : {})}
          >
            <span
              aria-hidden
              className={cn("h-1.5 w-1.5 shrink-0 rounded-full", fsView.dotClass)}
            />
            <span className="truncate">{fsView.copy}</span>
          </span>
        ) : (
          <>
            <span className="flex items-center gap-1.5 truncate text-[11px] text-ink-3">
              <span className="truncate">{label}</span>

              {!needs_reauth && !sendOnly && relative ? (
                <>
                  <span aria-hidden>·</span>
                  <span className="shrink-0">
                    <Trans>sync {relative} fa</Trans>
                  </span>
                </>
              ) : null}

              <span aria-hidden>·</span>

              <span className="inline-flex shrink-0 items-center gap-1">
                <ConnectorStatus
                  variant="dot"
                  signals={{ needsReauth: needs_reauth, lifecycleStatus }}
                />
                {needs_reauth ? (
                  <span className="text-warning">
                    <Trans>Token scaduto</Trans>
                  </span>
                ) : lifecycleStatus === "pending" ||
                  lifecycleStatus === "ingesting" ||
                  lifecycleStatus === "processing" ? (
                  <span className="text-accent">
                    <Trans>Sincronizzazione in corso…</Trans>
                  </span>
                ) : lifecycleStatus === "unhealthy" ? (
                  <span className="text-error">
                    <Trans>Ingestione fallita</Trans>
                  </span>
                ) : (
                  <span>
                    <Trans>Attivo</Trans>
                  </span>
                )}
              </span>
            </span>

            {/* Drive-only auto-sync badge (V1 audit G1). The query fires
                ONLY for drive rows with a recognised provider — every
                other capability (database, email, messaging, …) skips
                the BE round-trip entirely. ``needs_reauth`` suppresses
                the badge because the reconnect CTA owns the row in that
                state. ``asDriveProvider`` (the SoT narrowing helper in
                ``@ando/core``) returns a typed ``DriveProvider``, so no
                cast at the call site. */}
            {(() => {
              if (connection.capability !== "drive" || needs_reauth) return null;
              const driveProvider = asDriveProvider(vendorKey);
              if (!driveProvider) return null;
              return (
                <DriveAutoSyncSubLine provider={driveProvider} connectionKey={connection_key} />
              );
            })()}
          </>
        )}
      </div>

      <div className={cn("flex shrink-0 items-center gap-1", busy && "pointer-events-none")}>
        {/* Primary CTA — reauth wins; otherwise the manage button is shown ONLY
            when the connector actually has a manage surface (progressive
            disclosure). Connectors with ``manage: null`` render no primary
            button — no more "Gestisci →" that opens a "coming soon" toast. */}
        {needs_reauth ? (
          <Button
            variant="primary"
            size="sm"
            onClick={onReauth}
            disabled={busy}
            className="bg-warning from-warning to-warning text-white shadow-none hover:brightness-[1.05]"
          >
            <Trans>Riconnetti</Trans>
          </Button>
        ) : manifest.manage !== null && !sendOnly && connection.can_sync !== false ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onManage}
            disabled={busy}
            className="font-medium text-[11px] text-ink-2 hover:text-ink-1"
          >
            {manifest.manage === "syncNow" ? (
              <Trans>Sincronizza</Trans>
            ) : (
              <>
                <Settings aria-hidden />
                <Trans>Configura</Trans>
              </>
            )}
          </Button>
        ) : null}

        {/* Secondary actions. Rule: ≤ 2 actions (reconnect + disconnect) → show
            them INLINE as icon buttons (the row has room, a menu is overkill).
            A row with EXTRA actions (DB/ERP: schema / test / edit → > 2) keeps
            the kebab. */}
        {extraMenuItems ? (
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={i18n._(msg`Altre azioni`)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                disabled={busy}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-md",
                  "text-ink-3 hover:bg-black/[0.04] hover:text-ink-1",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                )}
              >
                <MoreVertical className="h-4 w-4" aria-hidden />
              </button>
            </PopoverTrigger>

            <PopoverContent align="end" role="menu" className="w-56 p-1.5">
              {extraMenuItems(() => setMenuOpen(false))}
              <hr className="my-1 h-px border-0 bg-hairline" />
              {manifest.reconnectable ? (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleReauthFromMenu}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] text-ink-1",
                      "hover:bg-black/[0.04]",
                      "focus-visible:bg-black/[0.04] focus-visible:outline-none",
                    )}
                  >
                    <RefreshCcw className="h-3.5 w-3.5 text-ink-3" aria-hidden />
                    <Trans>Riconnetti</Trans>
                  </button>
                  <hr className="my-1 h-px border-0 bg-hairline" />
                </>
              ) : null}
              <button
                type="button"
                role="menuitem"
                onClick={handleDisconnect}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left font-medium text-[12.5px] text-error",
                  "hover:bg-error/10",
                  "focus-visible:bg-error/10 focus-visible:outline-none",
                )}
              >
                <Unplug className="h-3.5 w-3.5" aria-hidden />
                {manifest.removeKind === "folder" ? (
                  <Trans>Rimuovi cartella</Trans>
                ) : (
                  <Trans>Disconnetti</Trans>
                )}
              </button>
            </PopoverContent>
          </Popover>
        ) : (
          <>
            {/* Reconnect — inline; skipped when the prominent reauth CTA already covers it. */}
            {manifest.reconnectable && !needs_reauth ? (
              <Button variant="ghost" size="sm" onClick={onReauth} disabled={busy}>
                <RefreshCcw aria-hidden />
                <Trans>Riconnetti</Trans>
              </Button>
            ) : null}
            {/* Disconnect — inline, destructive (red on hover). ``Unplug`` = the
                inverse of Ando's "Plug / connect" metaphor (clearer than a trash bin). */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onDisconnect}
              disabled={busy}
              className="text-ink-3 hover:bg-error/10 hover:text-error focus-visible:ring-error/45"
            >
              <Unplug aria-hidden />
              {manifest.removeKind === "folder" ? (
                <Trans>Rimuovi cartella</Trans>
              ) : (
                <Trans>Disconnetti</Trans>
              )}
            </Button>
          </>
        )}
      </div>
    </li>
  );
};
ConnectedAccountRow.displayName = "ConnectedAccountRow";

// ──────────────────────────────────────────────────────────────────────
// SyncStore wiring helpers
// ──────────────────────────────────────────────────────────────────────

/**
 * Map a connection's ``capability`` to the canonical ``SyncSource`` the
 * unified ``SyncStore`` uses. Three connectors share a capability with
 * the store's ``"chat"`` bucket (messaging); two share with ``"docs"``;
 * email is 1:1. Anything we don't recognise falls back to ``"other"``
 * so the pill stays quiet rather than mis-attributing a job.
 */
function syncSourceFor(connection: ConnectionSummary): SyncSource {
  switch (connection.capability) {
    case "email":
      return "email";
    case "messaging":
    case "chat":
      return "chat";
    case "drive":
    case "docs":
      return "docs";
    case "filesystem":
      return "filesystem";
    default:
      return "other";
  }
}

/**
 * Predicate that decides whether a ``SyncJob.identifier`` belongs to
 * THIS connection. Tries the most specific field first
 * (``email_address`` / ``connection_key``) and falls back to vendor
 * match — covering V1's mixed shape across drive / chat / email jobs.
 *
 * Takes only the connection fields it actually reads (not the full
 * ``ConnectionSummary``) so callers can memoise the predicate against
 * stable primitives instead of the full row reference — heartbeat
 * refreshes on ``last_used_at`` / ``metadata.status`` no longer thrash
 * the ``useLatestJobFor`` selector.
 */
interface MatchableConnection {
  readonly connection_key: ConnectionSummary["connection_key"];
  readonly provider?: ConnectionSummary["provider"];
  readonly vendor?: ConnectionSummary["vendor"];
}

function matchesConnection(
  id: {
    readonly provider?: string;
    readonly email_address?: string;
    readonly connection_key?: string;
  },
  connection: MatchableConnection,
): boolean {
  if (
    id.email_address &&
    connection.connection_key &&
    id.email_address === connection.connection_key
  ) {
    return true;
  }
  if (
    id.connection_key &&
    connection.connection_key &&
    id.connection_key === connection.connection_key
  ) {
    return true;
  }
  // Vendor-only match — last-resort fallback for legacy chat workspaces
  // where the BE emits a job WITHOUT a connection_key/email_address. It
  // MUST only fire when the job identifier itself is missing the
  // account-specific fields — otherwise a Google Drive sync on
  // ``andrea@gmail.com`` would light up the pill on EVERY drive row
  // (martina@gmail, andrea@ando-ai) just because they share the
  // ``google_drive`` provider. June 4 2026 incident: 3 google_drive
  // rows all showing "Sync" pill while only one account was syncing.
  if (
    id.provider &&
    !id.email_address &&
    !id.connection_key &&
    id.provider === (connection.provider ?? connection.vendor)
  ) {
    return true;
  }
  return false;
}
